/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import { jsx, css } from '@emotion/react';
import Table, { Cell, VisibleOnRowHover, Column } from 'components/table';
import DropdownMenu, { DropdownItem } from '@atlaskit/dropdown-menu';
import { FormattedMessage, FormattedNumber, useIntl } from 'components/i18n';
import { MoreDropdownMenu } from 'components/dropdown-menu';
import { queryCache, useQuery } from 'react-query';
import api from 'libs/api';
import { PageWrapper } from 'components/page';
import PageHeader from '@atlaskit/page-header';
import TextField from 'components/textfield';
import Button, { ButtonGroup } from '@atlaskit/button';
import { useNotifications } from 'components/notification';
import {useConfirm} from 'components/modal-dialog';

import useSaleItemModal from './components/useSaleItemModal';
import { SaleItem } from './types';
import useSearchable from '../../hooks/useSearchable';

type Props = {}

export const MoreDropdownCell: React.FC<any> = React.memo(
  ({ item, onEdit, onDelete }) => {
    return (
      <VisibleOnRowHover>
        <MoreDropdownMenu>
            <DropdownItem onClick={() => onEdit(item)}>
              <FormattedMessage
                id="sale.SaleItemsPage.options.edit"
                defaultMessage="Edit"
              />
            </DropdownItem>
              <DropdownItem onClick={() => onDelete(item)}>
                <FormattedMessage
                  id="sale.SaleItemsPage.options.delete"
                  defaultMessage="Delete"
                />
              </DropdownItem>
        </MoreDropdownMenu>
      </VisibleOnRowHover>
    );
  }
);

const fetchSaleItems = async (key: string) => {
  return api.get('/sale-items');
};
const deleteSaleItem = async (id: string) =>
  api.delete(`/sale-items/${id}`);

const SaleItemsPage: React.FC<Props> = (props) => {
  const intl = useIntl();
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const { confirmDelete } = useConfirm();

  const { data, error, isLoading } = useQuery(
    ['saleItems'],
    fetchSaleItems
  );

  const [search, setSearch] = useState('');
  const saleItems: SaleItem[] = useSearchable(data, search, {
    keys: ['name', 'description']
  }) || [];

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value),[]);

  const showSaleItemModal = useSaleItemModal();

  const handleNewItem = useCallback(() => {
    showSaleItemModal({
      saleItem: {type: 'service', name: '', isNew: true},
      isNew: true,
      onSubmit: async (saleItem: SaleItem) => {
        showSuccessNotification(
          <FormattedMessage
            id="sale.SaleItemsPage.notifications.created.success.title"
            defaultMessage="Sale item created"
          />)
      },
    });
  },[]);
  const handleEdit = useCallback((item) => {
    showSaleItemModal({
      saleItem: item,
      onSubmit: async (saleItem: SaleItem) => {
        showSuccessNotification(
            <FormattedMessage
              id="sale.SaleItemsPage.notifications.updated.success.title"
              defaultMessage="Sale item updated"
            />)
      },
    });
  },[]);

  const handleDelete = useCallback(async (saleItem: SaleItem) => {
    confirmDelete({
      title: (
        <FormattedMessage
          id="sale.SaleItemsPage.modals.delete.title`"
          defaultMessage="Delete this sale item?"
        />
      ),
      content: (
        <FormattedMessage
          id="sale.SaleItemsPage.modals.delete.content`"
          defaultMessage={`You are about to delete sale item "{name}".`}
          values={{ name: saleItem.name }}
        />
      ),
      onConfirm: async () => {
        try {
          await deleteSaleItem(saleItem.id!);
          showSuccessNotification(
            <FormattedMessage
              id="sale.SaleItemsPage.notifications.delete.success.title`"
              defaultMessage="Sale item deleted"
            />
          );
        } catch (e) {
          showErrorNotification(e.message);
        }
        queryCache.invalidateQueries('saleItems');
      },
    });
  }, []);

  const columns: Column<SaleItem>[] = useMemo(() => [
      {
        Header: <FormattedMessage id="sale.SaleItemsPage.headings.name" defaultMessage="Name"/>,
        accessor: 'name',
        width: 240,
        Cell: ({row: {original: saleItem}}) => <Cell primaryText={saleItem.name} secondaryText={saleItem.description}/>
      },
      {
        Header: <FormattedMessage id="sale.SaleItemsPage.headings.price" defaultMessage="Price"/>,
        align: 'right',
        accessor: 'price',
        width: 100,
        Cell: ({value}) => value ? <FormattedNumber value={value}/> : <span/>,
      },
      {
        id: 'actions',
        Header: "",
        minWidth: 56,
        width: 56,
        align: 'right',
        accessor: 'id',
        Cell: ({row: {original: saleItem}}) => <MoreDropdownCell onEdit={handleEdit} onDelete={handleDelete} item={saleItem}/>
      },
    ],[]);

  const actionsContent = React.useMemo(() => {
      const searchLabel = intl.formatMessage({
        id: 'sale.SaleItemsPage.search',
        defaultMessage: 'Search',
        description: 'Search placeholder',
      });

      const itemsContainer = css`
      display: flex;
      align-items: center;
      justify-content: flex-end;
    `;

      return (
        <div css={itemsContainer}>
          <div css={css`flex: 0 0 300px; margin-right: 1rem`}>
            <TextField placeholder={searchLabel} isCompact aria-label={searchLabel} // onChange={handleSearch}
              onChange={handleSearch}
            />
          </div>
          <Button appearance="primary" onClick={() => handleNewItem()}>
            <FormattedMessage
              id="sale.SaleItemsPage.create"
              defaultMessage="Create item"
            />
          </Button>
        </div>
      )}
    ,[intl]);

  return (
    <PageWrapper>
      <PageHeader actions={actionsContent} ><FormattedMessage id="sale.SaleItemsPage.title" defaultMessage="Product & services" />
      </PageHeader>
      <Table<SaleItem> columns={columns} data={saleItems}/>
    </PageWrapper>
    );
};

export default SaleItemsPage;
